import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { mcpService } from '../Services/mcpService';
import { RootState, useAppSelector } from '../store';

export interface McpConfigFormData {
    title: string;
    description: string;
    serverUrl: string;
    transport: 'http';
    authType: 'none' | 'api_key' | 'oauth';
    authData: Record<string, any>;
    timeout: number;
}

export interface McpConfigType {
    identifier: string;
    title: string;
    description: string;
    serverUrl: string;
    transport: string;
    authType: string;
    authData: Record<string, any>;
    timeout: number;
    lastSync: string | null;
    lastError: string | null;
    toolsCount: number;
}

type UseMcpConfigOptions = {
    onSaved?: () => void;
};

const useMcpConfig = ({ onSaved }: UseMcpConfigOptions = {}) => {
    const { configIdentifier } = useParams();
    const isNew = configIdentifier === 'new';
    const navigate = useNavigate();
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);

    const [activeTab, setActiveTab] = useState(0);
    const isMobile = typeof window !== 'undefined' ? window.innerWidth < 768 : false;
    const boxHeightClassesStr = isMobile ? 'h-[calc(var(--app-height)-70px)]' : 'h-[calc(var(--app-height)-140px)]';

    const [formData, setFormData] = useState<McpConfigFormData>({
        title: '',
        description: '',
        serverUrl: '',
        transport: 'http',
        authType: 'none',
        authData: {},
        timeout: 30,
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isLoading, setIsLoading] = useState(false);
    const [config, setConfig] = useState<McpConfigType | null>(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [initialSnapshot, setInitialSnapshot] = useState<McpConfigFormData | null>(null);

    const loadConfig = useCallback(async () => {
        if (isNew || !configIdentifier) return;
        setIsLoading(true);
        try {
            const response = await mcpService.getConfig(configIdentifier);
            const configData = response.data;
            setConfig(configData);
            setFormData({
                title: configData.title,
                description: configData.description,
                serverUrl: configData.serverUrl,
                transport: configData.transport,
                authType: configData.authType,
                authData: configData.authData || {},
                timeout: configData.timeout,
            });
            setInitialSnapshot({
                title: configData.title || '',
                description: configData.description || '',
                serverUrl: configData.serverUrl || '',
                transport: (configData.transport as 'http') || 'http',
                authType: (configData.authType as 'none' | 'api_key' | 'oauth') || 'none',
                authData: configData.authData || {},
                timeout: typeof configData.timeout === 'number' ? configData.timeout : 30,
            });
        } catch (error) {
            console.error('Error loading MCP config:', error);
            toast.error(
                i18n.translate({
                    id: 'load.error',
                    sourceName: 'MCP',
                }),
            );
            navigate('/admin/assistantfunctions?tab=mcp');
        } finally {
            setIsLoading(false);
        }
    }, [configIdentifier, isNew, navigate, i18n]);

    useEffect(() => {
        loadConfig();
    }, [loadConfig]);

    // track unsaved changes
    useEffect(() => {
        if (!initialSnapshot) {
            // for new configs, set baseline on first render
            if (isNew) setInitialSnapshot(formData);
            return;
        }
        try {
            const current = JSON.stringify(formData);
            const baseline = JSON.stringify(initialSnapshot);
            setHasUnsavedChanges(current !== baseline);
        } catch {
            // fallback: mark as changed on error
            setHasUnsavedChanges(true);
        }
    }, [formData, initialSnapshot, isNew]);

    const handleInputChange = (field: keyof McpConfigFormData, value: any) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field as string]) {
            setErrors((prev) => ({ ...prev, [field as string]: '' }));
        }
    };

    const handleAuthDataChange = (field: string, value: any) => {
        setFormData((prev) => ({
            ...prev,
            authData: { ...prev.authData, [field]: value },
        }));
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.title.trim()) {
            newErrors.title = i18n.translate({
                id: 'validation.title.required',
                sourceName: 'MCP',
            });
        }

        if (!formData.serverUrl.trim()) {
            newErrors.serverUrl = i18n.translate({
                id: 'validation.serverUrl.required',
                sourceName: 'MCP',
            });
        } else if (!formData.serverUrl.startsWith('http')) {
            newErrors.serverUrl = i18n.translate({
                id: 'validation.serverUrl.invalid',
                sourceName: 'MCP',
            });
        }

        if (formData.authType === 'api_key' && !formData.authData.apiKey) {
            newErrors.apiKey = i18n.translate({
                id: 'validation.apiKey.required',
                sourceName: 'MCP',
            });
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSyncTools = async (config: any) => {
        const loadingToast = toast.loading(
            i18n.translate({
                id: 'configs.sync.loading',
                sourceName: 'MCP',
            }),
        );
        try {
            const response = await mcpService.syncTools(config.identifier);
            toast.dismiss(loadingToast);
            if (response.data.success) {
                toast.success(
                    i18n.translate({
                        id: 'configs.sync.success',
                        params: { created: response.data.new, updated: response.data.synced, removed: response.data.removed },
                        sourceName: 'MCP',
                    }),
                );
            } else {
                toast.error(
                    i18n.translate({
                        id: 'configs.sync.failed',
                        params: { message: response.data.message },
                        sourceName: 'MCP',
                    }),
                );
            }
        } catch (error) {
            console.error('Failed to sync MCP tools:', error);
            toast.error(
                i18n.translate({
                    id: 'configs.sync.error',
                    sourceName: 'MCP',
                }),
            );
        }
    };

    const handleSave = async () => {
        if (!validateForm()) return;
        const loadingToast = toast.loading(
            i18n.translate({
                id: isNew ? 'create.loading' : 'update.loading',
                sourceName: 'MCP',
            }),
        );
        try {
            if (isNew) {
                const response = await mcpService.createConfig(formData);
                setHasUnsavedChanges(false);
                toast.success(
                    i18n.translate({
                        id: 'create.success',
                        sourceName: 'MCP',
                    }),
                );
                onSaved?.();
                navigate(`/admin/mcpconfig/${response.data.identifier}`);
            } else if (configIdentifier) {
                const response = await mcpService.updateConfig(configIdentifier, formData);
                setHasUnsavedChanges(false);
                toast.success(
                    i18n.translate({
                        id: 'update.success',
                        sourceName: 'MCP',
                    }),
                );
                if(response.data) {
                    const configData = response.data;
                    setInitialSnapshot({
                        title: configData.title || '',
                        description: configData.description || '',
                        serverUrl: configData.serverUrl || '',
                        transport: (configData.transport as 'http') || 'http',
                        authType: (configData.authType as 'none' | 'api_key' | 'oauth') || 'none',
                        authData: configData.authData || {},
                        timeout: typeof configData.timeout === 'number' ? configData.timeout : 30,
                    });
                }
            }
            onSaved?.();
        } catch (error: any) {
            console.error('Error saving MCP config:', error);
            toast.error(
                i18n.translate({
                    id: 'save.error',
                    sourceName: 'MCP',
                }),
            );
        } finally {
            toast.dismiss(loadingToast);
        }
    };

    const handleTestConnection = async () => {
        if (!configIdentifier) {
            return;
        }
        const loadingToast = toast.loading(
            i18n.translate({
                id: 'test.loading',
                sourceName: 'MCP',
            }),
        );
        try {
            // Test the connection
            const testResponse = await mcpService.testConnection(configIdentifier);

            toast.dismiss(loadingToast);
            if (testResponse.data.success) {
                toast.success(
                    i18n.translate({
                        id: 'test.success',
                        sourceName: 'MCP',
                    }),
                );
            } else {
                toast.error(
                    i18n.translate({
                        id: 'configs.test.failed',
                        params: { message: testResponse.data.message },
                        sourceName: 'MCP',
                    }),
                );
            }
        } catch (error: any) {
            console.error('Connection test failed:', error);
            toast.error(
                i18n.translate({
                    id: 'test.error',
                    sourceName: 'MCP',
                }),
            );
        }
    };

    const handleCancel = () => {
        navigate('/admin/assistantfunctions?tab=mcp');
    };

    return {
        // state
        formData,
        errors,
        isLoading,
        config,
        isNew,
        activeTab,
        isMobile,
        boxHeightClassesStr,
        hasUnsavedChanges,
        // actions
        handleSave,
        handleTestConnection,
        handleCancel,
        handleInputChange,
        handleAuthDataChange,
        updateFormData: (updates: Partial<McpConfigFormData>) => setFormData((prev) => ({ ...prev, ...updates })),
        updateAuthData: (authUpdates: Record<string, any>) =>
            setFormData((prev) => ({ ...prev, authData: { ...prev.authData, ...authUpdates } })),
        setActiveTab,
        setFormData,
        setErrors,
        handleSyncTools,
    };
};

export default useMcpConfig;
